import { inject, onScopeDispose, ref, watch, type Ref } from 'vue'

import { settingsService } from '@/services/settingsService'

import type { Map as MapLibreMap } from 'maplibre-gl'

export type MapBounds = { west: number; east: number; south: number; north: number }

/**
 * Composable that provides:
 * - `lockListToMap` — a persisted reactive boolean (settingsKey determines storage key)
 * - `currentMapBounds` — the current visible map viewport bounds, updated on every `moveend`
 *
 * Intended for list panels that want to filter their content to what is visible on the map.
 * Must be used inside a component that has `mapInstance` and `mapLoaded` in its provide chain
 * (typically WorkspaceMapContainer or MapLibreRoot).
 */
export function useSyncToMap(settingsKey: string, defaultValue = false) {
  const mapInstance = inject<Ref<MapLibreMap | null>>('mapInstance', ref(null))
  const mapLoaded = inject<Ref<boolean>>('mapLoaded', ref(false))
  const mapHeaderHeight = inject<Ref<number>>('mapHeaderHeight', ref(48))
  const mapBottomInset = inject<Ref<number>>('mapBottomInset', ref(0))

  const lockListToMap = ref(settingsService.getSetting<boolean>(settingsKey) ?? defaultValue)
  let applyingRemoteSetting = false
  const unsubscribeSetting = settingsService.subscribe(({ key, value, source }) => {
    if (source !== 'remote' || key !== settingsKey || typeof value !== 'boolean') return
    applyingRemoteSetting = true
    lockListToMap.value = value
    applyingRemoteSetting = false
  })
  onScopeDispose(unsubscribeSetting)

  watch(
    lockListToMap,
    (val) => {
      if (applyingRemoteSetting) return
      settingsService.saveSetting(settingsKey, val)
    },
    { flush: 'sync' },
  )

  const currentMapBounds = ref<MapBounds | null>(null)

  const updateMapBounds = () => {
    const map = mapInstance.value
    if (!map) return
    const bounds = map.getBounds()
    const canvas = map.getCanvas()
    const visibleNorth = map.unproject([0, mapHeaderHeight.value]).lat
    const visibleSouth = map.unproject([0, canvas.clientHeight - mapBottomInset.value]).lat
    currentMapBounds.value = {
      west: bounds.getWest(),
      east: bounds.getEast(),
      south: visibleSouth,
      north: visibleNorth,
    }
  }

  let listenerAdded = false
  watch(
    [mapLoaded, mapInstance] as const,
    ([loaded]) => {
      if (!loaded || !mapInstance.value || listenerAdded) return
      updateMapBounds()
      mapInstance.value.on('moveend', updateMapBounds)
      listenerAdded = true
    },
    { immediate: true },
  )

  return { lockListToMap, currentMapBounds }
}
